import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaUser,
  FaCar,
  FaSuitcase,
  FaChevronLeft,
  FaChevronRight,
  FaSearch,
} from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import { cars } from "../../services/api";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";
import { slugifyCar } from "../../utils/slugify";


const ITEMS_PER_PAGE = 5;

const SearchResultsList = ({ filters }) => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [animated, setAnimated] = useState(false);
  const [availableCars, setAvailableCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");  useEffect(() => {
    const fetchAvailableCars = async () => {
      try {
        const response = await cars.getAll();
        
        let dynamicPriceMap = {};
        
        // Check if we already have dynamic prices from search
        if (filters.dynamicPriceMap && Object.keys(filters.dynamicPriceMap).length > 0) {
          // Use the dynamic prices passed from the search form
          dynamicPriceMap = filters.dynamicPriceMap;
          console.log('Using dynamic price map from filters:', 
            Object.keys(filters.dynamicPriceMap).length, 'entries available');
        } else {
          console.log('No dynamic prices in filters, fetching today\'s prices');
          // If no specific dates were searched, get today's prices
          const today = new Date();
          const todayStr = today.toISOString();
          
          // Fetch current price for all cars in one request
          const availabilityResponse = await cars.getAvailable({
            startDate: todayStr,
            endDate: todayStr,
            includeAll: true
          });
          
          // Create a map of car IDs to dynamic prices
          if (availabilityResponse.data && availabilityResponse.data.length) {
            console.log('Received today\'s pricing data for', availabilityResponse.data.length, 'cars');
            availabilityResponse.data.forEach(car => {
              dynamicPriceMap[car._id] = car.dynamicPrice;
            });
          } else {
            console.warn('No pricing data received for today');
          }
        }
        
        const fetchedCars = response.data.map((car) => {
          // Get the dynamic price for this car if available
          const carDynamicPrice = dynamicPriceMap[car._id];
          
          return {
            ...car,
            name: `${car.make} ${car.model} ${
              car.transmission.charAt(0).toUpperCase() + car.transmission.slice(1)
            } (or Similar)`,
            // Handle both server uploaded images and local assets
            image: car.images[0].includes('/uploads/') 
              ? `${car.images[0]}`
              : require(`../../assets${car.images[0]}`),
            price: carDynamicPrice || car.basePrice, // Use dynamic price if available
            dynamicPrice: carDynamicPrice || car.basePrice, // Add dynamic price property
            persons: car.seats,
          };
        });

        setAvailableCars(fetchedCars);
      } catch (err) {
        console.error("Error fetching cars:", err);
        setError("Failed to fetch cars. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchAvailableCars();
    window.scrollTo({ top: 0, behavior: "smooth" });
    setCurrentPage(1);
    setAnimated(true);
    const timer = setTimeout(() => setAnimated(false), 500);
    return () => clearTimeout(timer);
  }, [filters.dateRange]);

  // Remove duplicates based on stringId
  const uniqueCars = availableCars.reduce((acc, car) => {
    if (!acc.has(car.stringId)) {
      acc.set(car.stringId, car);
    }
    return acc;
  }, new Map());

  const disclaimers = [
    "Free cancellation",
    "No credit card fees",
    "Theft Protection",
    "Unlimited Mileage (within Mykonos isl.)",
  ];

  // Filtering logic
  const filteredResults = Array.from(uniqueCars.values()).filter((car) => {
    // Search filter
    if (searchTerm) {
      const carName = `${car.make} ${car.model}`.toLowerCase();
      if (!carName.includes(searchTerm.toLowerCase())) {
        return false;
      }
    }

    // Category filter
    if (filters.category && filters.category.length > 0) {
      const matchesCategory = filters.category.some(category => {
        // Handle different vehicle type groups
        if (["Economy", "Electric", "Standard", "Cabrio", "Special"].includes(category)) {
          // Cars group
          return category === car.category;
        } else if (["Mini Vans", "Jeep-SUV-Family"].includes(category)) {
          // Vans & Trucks group
          return category === car.category;
        } else if (["Motorbikes", "ATV-Quads"].includes(category)) {
          // ATV / Motorbikes group
          return category === car.category;
        } else if (category === "Jeep Wrangler") {
          // Jeep Wrangler group
          return car.category === "Jeep Wrangler";
        }
        return false;
      });
      
      if (!matchesCategory) {
        return false;
      }
    }

    // Transmission filter
    if (filters.transmission && car.transmission.toLowerCase() !== filters.transmission.toLowerCase()) {
      return false;
    }

    // Passengers filter
    if (filters.passengers) {
      const [min, max] = filters.passengers.split(" ")[0].split("-").map(Number);
      if (car.persons < min || car.persons > max) {
        return false;
      }
    }

    // Price range filter
    if (filters.priceRange && filters.priceRange.length === 2) {
      const [minPrice, maxPrice] = filters.priceRange;
      const price = car.basePrice || car.price;
      if (price < minPrice || price > maxPrice) {
        return false;
      }
    }

    return true;
  });

  // Reset pagination when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Sort results by price
  filteredResults.sort(
    (a, b) => (a.basePrice || a.price) - (b.basePrice || b.price)
  );

  // Pagination calculation
  const totalPages = Math.ceil(filteredResults.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedResults = filteredResults.slice(
    startIndex,
    startIndex + ITEMS_PER_PAGE
  );

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
      },
    },
  };

  const handlePageChange = (newPage) => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setCurrentPage(newPage);
  };

  const handleRentNow = (e, car) => {
    e.preventDefault();
    window.scrollTo({ top: 0, behavior: "smooth" });

    // Get effective display locations (custom text if 'other' is selected)
    const effectivePickupLocation =
      filters.pickupLocation === "other"
        ? filters.customPickupLocation
        : filters.pickupLocation === "airport"
        ? "Airport of Mykonos (JMK)"
        : filters.pickupLocation === "central"
        ? "Mykonos Central Office"
        : filters.pickupLocation === "new-port"
        ? "Mykonos New Port (Tourlos)"
        : filters.pickupLocation === "old-port"
        ? "Mykonos Old Port"
        : filters.pickupLocation;

    const effectiveDropoffLocation =
      filters.dropoffLocation === "other"
        ? filters.customDropoffLocation
        : filters.dropoffLocation === "airport"
        ? "Airport of Mykonos (JMK)"
        : filters.dropoffLocation === "central"
        ? "Mykonos Central Office"
        : filters.dropoffLocation === "new-port"
        ? "Mykonos New Port (Tourlos)"
        : filters.dropoffLocation === "old-port"
        ? "Mykonos Old Port"
        : filters.dropoffLocation;

          const slug = slugifyCar(car);


    navigate(`/car-details/${slug}`, {
      state: {
        car,
        formData: {
          dateRange: filters.dateRange,
          pickupLocation: effectivePickupLocation,
          dropoffLocation: effectiveDropoffLocation,
          originalPickupLocation: filters.pickupLocation,
          originalDropoffLocation: filters.dropoffLocation,
          customPickupLocation: filters.customPickupLocation,
          customDropoffLocation: filters.customDropoffLocation,
          age: filters.age || "26+",
          couponCode: filters.couponCode || "",
          passengers: filters.passengers || "",
          transmission: filters.transmission || "",
        },
      },
    });
  };

  return (
    <div className="Aleo">
      <h2 className="text-2xl font-bold mb-6">
        Search Results ({filteredResults.length} vehicles found)
      </h2>

      {/* Search Input */}
      <div className="mb-6 relative">
        <div className="relative">
          <input
            type="text"
            placeholder="Search by make or model..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full p-3 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
          />
          <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
        </div>
      </div>

      <AnimatePresence mode="wait">
        {loading ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <LoadingSpinner message="Searching for available vehicles..." />
          </motion.div>
        ) : error ? (
          <motion.div
            className="bg-red-50 border border-red-400 text-red-700 px-4 py-3 rounded"
            role="alert"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <p className="text-center">{error}</p>
          </motion.div>
        ) : (
          <motion.div
            key={
              currentPage +
              filters.category.join() +
              filters.transmission +
              filters.passengers +
              filters.priceRange.join()
            }
            className="space-y-6"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {paginatedResults.length > 0 ? (
              <>
                {paginatedResults.map((car, index) => (
                  <motion.div
                    key={`${car.name}-${car.category}`}
                    className="bg-white rounded-lg shadow-lg p-4 sm:p-6 flex flex-col lg:flex-row hover:shadow-2xl transition-shadow duration-300"
                    variants={itemVariants}
                    initial="hidden"
                    animate="visible"
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    {/* Car Image */}
                    <div className="w-full lg:w-1/3 mb-4 lg:mb-0">
                      <motion.img
                        src={car.image}
                        alt={car.name}
                        className="w-full h-48 object-contain transform hover:scale-105 transition-transform duration-300"
                        whileHover={{ scale: 1.05 }}
                      />
                    </div>

                    {/* Car Info */}
                    <div className="w-full lg:w-1/3 px-0 lg:px-6 mb-4 lg:mb-0">
                      <motion.h3
                        className="text-xl font-bold mb-4"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.2 }}
                      >
                        {car.name}
                      </motion.h3>
                      <div className="text-sm text-gray-500 mb-3">
                        Category: {car.category}
                      </div>
                      <motion.div
                        className="flex items-center justify-around lg:justify-start lg:space-x-6 text-gray-600 mb-4"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.3 }}
                      >
                        <div className="flex items-center">
                          <FaUser className="mr-2" />
                          <span>{car.persons}</span>
                        </div>
                        <div className="flex items-center">
                          <FaCar className="mr-2" />
                          <span>{car.doors}</span>
                        </div>
                        <div className="flex items-center">
                          <FaSuitcase className="mr-2" />
                          <span>{car.suitcases}</span>
                        </div>
                      </motion.div>
                      <div className="grid grid-cols-2 gap-2">
                        {disclaimers.map((disclaimer, index) => (
                          <motion.div
                            key={index}
                            className="flex items-start"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.4 + index * 0.1 }}
                          >
                            <span className="text-red-600 mr-2">•</span>
                            <span className="text-red-600 text-xs sm:text-sm">
                              {disclaimer}
                            </span>
                          </motion.div>
                        ))}
                      </div>
                    </div>

                    {/* Price and CTA */}
                    <div className="w-full lg:w-1/3 flex flex-col items-center justify-center px-0 lg:px-6 mt-4 lg:mt-0">
                      <motion.div
                        className="text-center mb-4"
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.5 }}
                      >
                        <div className="text-3xl font-bold text-blue-600">
                          €{car.price.toFixed(0)}.00
                        </div>
                        <div className="text-gray-500 text-sm">/per day</div>
                      </motion.div>
                      <motion.button
                        onClick={(e) => handleRentNow(e, car)}
                        className="w-full sm:w-auto bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-8 rounded transition duration-300 transform hover:scale-105"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        Rent Now
                      </motion.button>
                    </div>
                  </motion.div>
                ))}

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-center items-center space-x-2 sm:space-x-4 mt-8">
                    <button
                      onClick={() =>
                        handlePageChange(Math.max(1, currentPage - 1))
                      }
                      disabled={currentPage === 1}
                      className={`p-2 rounded-full flex items-center justify-center w-10 h-10 ${
                        currentPage === 1
                          ? "text-gray-400 cursor-not-allowed"
                          : "text-red-600 hover:bg-red-50"
                      }`}
                      aria-label="Previous page"
                    >
                      <FaChevronLeft />
                    </button>

                    <div className="flex space-x-1 sm:space-x-2">
                      {(() => {
                        // Generate array of page numbers to show
                        let pageNumbers = [];
                        // For mobile, we'll show fewer page numbers
                        const maxVisible = window.innerWidth < 640 ? 3 : 5;
                        
                        if (totalPages <= maxVisible) {
                          // Show all pages if there are only a few
                          pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);
                        } else {
                          // Always include first page
                          pageNumbers.push(1);
                          
                          // Middle pages
                          let startPage = Math.max(2, currentPage - Math.floor((maxVisible - 2) / 2));
                          let endPage = Math.min(totalPages - 1, startPage + maxVisible - 3);
                          
                          // Adjust if we're near the end
                          if (endPage === totalPages - 1) {
                            startPage = Math.max(2, totalPages - maxVisible + 1);
                          }
                          
                          // Add ellipsis if needed
                          if (startPage > 2) {
                            pageNumbers.push('...');
                          }
                          
                          // Add middle pages
                          for (let i = startPage; i <= endPage; i++) {
                            pageNumbers.push(i);
                          }
                          
                          // Add ellipsis if needed
                          if (endPage < totalPages - 1) {
                            pageNumbers.push('...');
                          }
                          
                          // Always include last page
                          pageNumbers.push(totalPages);
                        }
                        
                        return pageNumbers.map((page, index) => 
                          page === '...' ? (
                            <span key={`ellipsis-${index}`} className="w-8 h-8 flex items-center justify-center text-gray-600">
                              ...
                            </span>
                          ) : (
                            <button
                              key={page}
                              onClick={() => handlePageChange(page)}
                              className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full transition-colors duration-300 text-sm ${
                                currentPage === page
                                  ? "bg-red-600 text-white"
                                  : "text-gray-600 hover:bg-red-50"
                              }`}
                              aria-label={`Page ${page}`}
                              aria-current={currentPage === page ? "page" : undefined}
                            >
                              {page}
                            </button>
                          )
                        );
                      })()}
                    </div>

                    <button
                      onClick={() =>
                        handlePageChange(Math.min(totalPages, currentPage + 1))
                      }
                      disabled={currentPage === totalPages}
                      className={`p-2 rounded-full flex items-center justify-center w-10 h-10 ${
                        currentPage === totalPages
                          ? "text-gray-400 cursor-not-allowed"
                          : "text-red-600 hover:bg-red-50"
                      }`}
                      aria-label="Next page"
                    >
                      <FaChevronRight />
                    </button>
                  </div>
                )}
              </>
            ) : (
              <motion.div
                className="text-center py-8 text-gray-500 bg-white rounded-lg shadow-sm"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                No vehicles match your selected filters
              </motion.div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SearchResultsList;